/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  Typography,
  Table,
  Card,
  InputAdornment,
  IconButton,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputLabel,
  FormControl,
  Stack,
  Select,
  Divider,
  LinearProgress,
  TextField
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import dayjs from 'dayjs';
import { GetTransactionHistory } from '../network/Organization';
import { ExportMerchantTransactionHistoryReport } from '../network/Merchants';
import { isoDateFormatDmyhs, getDateINYMD } from '../utils/formatTime';
import {
  parseValidationErrorMessageWithBlobSupport,
  showFieldValueWithCheck
} from '../utils/Utilities';
import ExportDataModal from '../components/_dashboard/app/ExportDataModal';
import { NoDataAvailable } from '../components/_dashboard/app';
import { DateSelectorModal } from '../components/_dashboard/trendGraphs';
import { SnackBarAlert } from '../components/HelperComponents';

// ----------------------------------------------------------------------

let currentPage = 1;
let isHasTransactionDataHasNextPage = true;

export default function SoundBoxTransactions() {
  const cardRef = useRef(null);
  const [transactionDataLoading, setTransactionDataLoading] = useState(false);
  const [transactionExportLoading, setTransactionExportLoading] = useState(false);
  const [openExportModal, setOpenExportModal] = useState(false);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [dateFilter, setDateFilter] = useState({
    startDate: getDateINYMD(new Date()),
    endDate: getDateINYMD(new Date())
  });
  const [vpaSearchApplied, setVpaSearchApplied] = useState(false);
  const [searchedFilterType, setSearchedFilterType] = useState('vpa');
  const [searchedVpaValue, setSearchedVpaValue] = useState('');
  const [searchedPhoneNumberValue, setSearchedPhoneNumberValue] = useState('');
  const [searchedUserNameValue, setSearchedUserNameValue] = useState('');
  const [deviceTransactionsList, setDeviceTransactionsList] = useState([]);
  const [isSnackBarAlertOpen, setIsSnackBarAlertOpen] = useState(false);
  const [snackBarMetas, setSnackBarMetas] = useState({
    title: '',
    message: '',
    severity: 'info',
    handleClose: null
  });

  useEffect(() => {
    getTransactionHistory(currentPage, 20, 'LOAD', buildWhere());
    return () => {
      setDeviceTransactionsList([]);
      currentPage = 1;
      isHasTransactionDataHasNextPage = true;
    };
  }, []);

  const handleScroll = () => {
    const card = cardRef.current;
    if (card) {
      const scrollPercentage = (card.scrollTop / (card.scrollHeight - card.clientHeight)) * 100;
      if (scrollPercentage > 90 && isHasTransactionDataHasNextPage && !transactionDataLoading) {
        currentPage = currentPage + 1;
        getTransactionHistory(currentPage, 10, 'LOAD', buildWhere());
      }
    }
  };

  const buildWhere = (overrideDateFilter = null) => {
    const dates = overrideDateFilter || dateFilter;
    const where = {
      startDate: dates.startDate,
      endDate: dates.endDate
    };

    switch (searchedFilterType) {
      case 'vpa': {
        const vpa = searchedVpaValue.trim();
        if (vpa) where.vpa = vpa.toLowerCase();
        break;
      }
      case 'name': {
        const name = searchedUserNameValue.trim();
        if (name) where.name = name;
        break;
      }
      case 'phone': {
        const phoneNo = searchedPhoneNumberValue.trim();
        if (phoneNo) where.phone = phoneNo;
        break;
      }
      default:
        break;
    }
    return where;
  };

  const handleSearchBy = (field, value) => {
    const query = (value ?? '').trim();
    currentPage = 1;
    isHasTransactionDataHasNextPage = true;

    const where = {
      startDate: dateFilter.startDate,
      endDate: dateFilter.endDate
    };

    if (!query) {
      getTransactionHistory(1, 20, 'SEARCH', where);
      return;
    }
    switch (field) {
      case 'vpa':
        where.vpa = query.toLowerCase();
        break;
      case 'name':
        where.name = query;
        break;
      case 'phone':
        where.phone = query;
        break;
      default:
        break;
    }
    getTransactionHistory(1, 20, 'SEARCH', where);
  };

  const getSelectedFilterValue = () => {
    if (searchedFilterType === 'vpa') return searchedVpaValue;
    if (searchedFilterType === 'name') return searchedUserNameValue;
    if (searchedFilterType === 'phone') return searchedPhoneNumberValue;
    return '';
  };

  const handleSelectedFilterValueChange = (value) => {
    if (searchedFilterType === 'vpa') {
      setSearchedVpaValue(value);
      setVpaSearchApplied(false);
      return;
    }
    if (searchedFilterType === 'name') {
      setSearchedUserNameValue(value);
      return;
    }
    if (searchedFilterType === 'phone') {
      setSearchedPhoneNumberValue(value);
    }
  };

  const triggerSelectedFilterSearch = (value) => {
    if (searchedFilterType === 'vpa' && value?.trim()) {
      setVpaSearchApplied(true);
    } else {
      setVpaSearchApplied(false);
    }
    handleSearchBy(searchedFilterType, value);
  };

  const handleFilterTypeChange = (event) => {
    setSearchedFilterType(event.target.value);
    setVpaSearchApplied(false);
    setSearchedVpaValue('');
    setSearchedUserNameValue('');
    setSearchedPhoneNumberValue('');
    currentPage = 1;
    isHasTransactionDataHasNextPage = true;
    getTransactionHistory(1, 20, 'SEARCH', {
      startDate: dateFilter.startDate,
      endDate: dateFilter.endDate
    });
  };

  const handleFilterApply = ({ startDate, endDate }) => {
    setDateFilter({ startDate, endDate });
    currentPage = 1;
    isHasTransactionDataHasNextPage = true;

    const where = buildWhere({ startDate, endDate });
    getTransactionHistory(1, 20, 'SEARCH', where);
  };

  const handleExportTransactionHistory = async ({ startDate, endDate }) => {
    const merchantVpa = searchedVpaValue?.trim();
    if (!merchantVpa) return;

    const formattedStart = dayjs(startDate).format('YYYY-MM-DD');
    const formattedEnd = dayjs(endDate).format('YYYY-MM-DD');

    setTransactionExportLoading(true);
    try {
      const res = await ExportMerchantTransactionHistoryReport(
        merchantVpa,
        formattedStart,
        formattedEnd
      );
      const blob = new Blob([res?.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'MerchantTransactionHistory.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      const errorMessage = await parseValidationErrorMessageWithBlobSupport(error);
      setSnackBarMetas((prev) => ({
        ...prev,
        title: errorMessage,
        severity: 'error'
      }));
      setIsSnackBarAlertOpen(true);
    } finally {
      setTransactionExportLoading(false);
    }
  };

  function getTransactionHistory(pageNo, limit, type, whereObj) {
    setTransactionDataLoading(true);
    const { startDate, endDate, ...where } = whereObj;
    GetTransactionHistory(pageNo, limit, where, startDate, endDate)
      .then((res) => {
        setTransactionDataLoading(false);
        isHasTransactionDataHasNextPage = res?.data?.hasNextPage;
        if (type === 'LOAD') {
          setDeviceTransactionsList((prevList) => [...prevList, ...res?.data?.data]);
        } else if (type === 'SEARCH') {
          setDeviceTransactionsList(res?.data?.data ?? []);
        }
      })
      .catch((err) => {
        setTransactionDataLoading(false);
      });
  }

  return (
    <Box data-cy="transactions-section">
      <Box sx={{ width: '100%' }}>
        <Card
          sx={{
            width: '100%',
            background: '#FFFFFF',
            boxShadow: '10px 10px 19px 1px #EAEAFF',
            borderRadius: '20px',
            marginBottom: 1,
            height: '72vh',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'column', md: 'row' }}
            alignItems="center"
            justifyContent="space-between"
            sx={{ pt: 2, px: 3, pb: 1 }}
            spacing={2}
          >
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start">
              <FormControl size="small" sx={{ width: 200 }}>
                <InputLabel>Search By</InputLabel>
                <Select
                  value={searchedFilterType}
                  onChange={handleFilterTypeChange}
                  label="Search By"
                >
                  <MenuItem value="vpa">Merchant VPA</MenuItem>
                  <MenuItem value="name">Merchant Name</MenuItem>
                  {/* <MenuItem value="phone">Merchant Phone No.</MenuItem> */}
                </Select>
              </FormControl>

              <Typography> = </Typography>

              <FormControl size="small" sx={{ width: 300 }}>
                <TextField
                  size="small"
                  label={
                    searchedFilterType === 'vpa'
                      ? 'Enter VPA'
                      : searchedFilterType === 'name'
                        ? 'Enter Name'
                        : 'Enter Phone'
                  }
                  value={getSelectedFilterValue()}
                  onChange={(e) => handleSelectedFilterValueChange(e.target.value)}
                  inputProps={{ 'data-cy': 'transactions-search-vpa' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          data-cy="transactions-search-submit"
                          onClick={() => triggerSelectedFilterSearch(getSelectedFilterValue())}
                        >
                          <SearchOutlinedIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      triggerSelectedFilterSearch(getSelectedFilterValue());
                    }
                  }}
                />
              </FormControl>
            </Stack>
            <Box sx={{ display: 'flex', gap: 2, ml: 2 }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => setOpenFilterModal(true)}
                sx={{ minHeight: '5vh' }}
                startIcon={<FilterListIcon />}
              >
                Date Filter - {dateFilter.startDate === dateFilter.endDate
                  ? dateFilter.startDate
                  : `${dateFilter.startDate} to ${dateFilter.endDate}`}
              </Button>
              <Button
                variant="contained"
                color="whitebg"
                data-cy="export-transaction-history-button"
                onClick={() => setOpenExportModal(true)}
                sx={{ minHeight: '5vh', minWidth: 120 }}
                startIcon={<FileDownloadOutlinedIcon />}
                disabled={!vpaSearchApplied || transactionExportLoading}
              >
                {transactionExportLoading ? 'Exporting...' : 'Export Transaction History'}
              </Button>
            </Box>
          </Stack>
          <Divider sx={{ mx: 3 }} />
          {transactionDataLoading ? (
            <Box sx={{ px: 3, pb: 1 }}>
              <LinearProgress color="success" data-cy="transactions-loading" />
            </Box>
          ) : null}
          {deviceTransactionsList !== null && deviceTransactionsList !== undefined && deviceTransactionsList.length > 0 ? (
            <TableContainer
              sx={{
                maxHeight: 'calc(72vh - 100px)',
                overflowY: 'scroll',
                flex: 1,
                px: 1
              }}
              ref={cardRef}
              onScroll={handleScroll}
              data-cy="transactions-table-container"
            >
              <Table aria-label="transaction_table" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: '8vw', fontWeight: 900 }} align="left">
                      Merchant VPA
                    </TableCell>
                    <TableCell sx={{ minWidth: '8vw', fontWeight: 900 }} align="left">
                      Merchant Name
                    </TableCell>
                    <TableCell sx={{ minWidth: '8vw', fontWeight: 900 }} align="left">
                      Branch
                    </TableCell>
                    <TableCell sx={{ minWidth: '8vw', fontWeight: 900 }} align="center">
                      Amount
                    </TableCell>
                    <TableCell sx={{ minWidth: '8vw', fontWeight: 900 }} align="center">
                      Announcement
                    </TableCell>
                    <TableCell sx={{ minWidth: '8vw', fontWeight: 900 }} align="center">
                      Date & Time
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deviceTransactionsList?.map((row) => (
                    <TableRow
                      key={row?.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      data-cy="transactions-row"
                    >
                      <TableCell align="left"> {row?.vpa}</TableCell>
                      <TableCell align="left"> {row?.name}</TableCell>
                      <TableCell align="left"> {showFieldValueWithCheck(row?.branch)}</TableCell>
                      <TableCell align="center">
                        {' '}
                        Rs. {showFieldValueWithCheck(row?.amount)}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontWeight: 700,
                          color:
                            row?.latency && Object.keys(row.latency || {}).length > 0
                              ? '#0DA626' // green
                              : '#C30A0A' // red
                        }}
                      >
                        {row?.latency && Object.keys(row.latency || {}).length > 0 ? 'Yes' : 'No'}
                      </TableCell>

                      <TableCell align="center"> {isoDateFormatDmyhs(row?.created_at)} </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}

          {deviceTransactionsList?.length === 0 && !transactionDataLoading ? (
            <NoDataAvailable message="No transactions available" />
          ) : null}
        </Card>
      </Box>

      <ExportDataModal
        openDateRangeModal={openExportModal}
        handleClose={() => setOpenExportModal(false)}
        onAction={handleExportTransactionHistory}
      />

      <DateSelectorModal
        openDateSelectorModal={openFilterModal}
        handleClose={() => setOpenFilterModal(false)}
        onApply={handleFilterApply}
        isInterval={false}
        maxDays={30}
      />

      <SnackBarAlert
        open={isSnackBarAlertOpen}
        title={snackBarMetas?.title}
        message={snackBarMetas?.message}
        severity={snackBarMetas?.severity}
        handleClose={() => setIsSnackBarAlertOpen(false)}
      />
    </Box>
  );
}
